import { ClockIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useIsTouchPrimary } from "hooks/use-is-touch-primary";
import * as React from "react";
import {
    Button,
    ComboBox,
    ComboBoxStateContext,
    Group,
    Input,
    Label,
    ListBox,
    ListBoxItem,
    Popover,
} from "react-aria-components";
import {
    buildTimeOptions,
    DEFAULT_TIME_LIST_ANCHOR,
    normalizeTimeValue,
    TimeOption,
} from "./time-options";

interface TimePickerProps {
    label: string | null;
    value: string | null;
    onChange: (value: string | null) => void;
    disabled: boolean;
    nativeStepMinutes: number;
}

const NativeTimePicker: React.FC<TimePickerProps> = ({
    label,
    value,
    onChange,
    disabled,
    nativeStepMinutes,
}) => (
    <div className="w-full">
        <label>
            {label ? <div className="mb-1">{label}</div> : null}
            <input
                data-testid="time-picker-native"
                type="time"
                step={nativeStepMinutes * 60}
                value={normalizeTimeValue(value) ?? ""}
                disabled={disabled}
                onChange={e => onChange(e.target.value ? e.target.value : null)}
                className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
            />
        </label>
    </div>
);

const centerOptionInList = (list: HTMLElement | null, label: string): void => {
    if (!list) {
        return;
    }
    const option = Array.from(list.querySelectorAll('[role="option"]')).find(
        node => node.textContent === label,
    );
    if (option instanceof HTMLElement) {
        list.scrollTop =
            option.offsetTop - Math.max(0, (list.clientHeight - option.offsetHeight) / 2);
    }
};

const TimeListBox: React.FC<{ anchorValue: string }> = ({ anchorValue }) => {
    const state = React.useContext(ComboBoxStateContext);
    const listRef = React.useRef<HTMLDivElement>(null);
    const hasAnchored = React.useRef(false);
    React.useEffect(() => {
        if (hasAnchored.current || !state) {
            return;
        }
        hasAnchored.current = true;
        const anchorItem = state.collection.getItem(anchorValue);
        if (!anchorItem) {
            return;
        }
        state.selectionManager.setFocusedKey(anchorValue);
        centerOptionInList(listRef.current, anchorItem.textValue);
    });
    return (
        <ListBox
            ref={listRef}
            className="relative max-h-72 overflow-y-auto overscroll-contain outline-none"
            renderEmptyState={() => (
                <div className="px-3 py-2 text-gray-500">No matching times</div>
            )}>
            {(option: TimeOption) => (
                <ListBoxItem
                    id={option.value}
                    textValue={option.label}
                    className="cursor-pointer px-3 py-1 outline-none data-[focused]:bg-brand-100 data-[selected]:bg-brand-600 data-[selected]:text-white">
                    {option.label}
                </ListBoxItem>
            )}
        </ListBox>
    );
};

const DesktopTimePicker: React.FC<TimePickerProps> = ({
    label,
    value,
    onChange,
    disabled,
}) => {
    const normalizedValue = normalizeTimeValue(value);
    const options = buildTimeOptions(normalizedValue);
    return (
        <ComboBox
            className="w-full"
            aria-label={label ? undefined : "Time"}
            defaultItems={options}
            selectedKey={normalizedValue}
            onSelectionChange={key => onChange(key === null ? null : String(key))}
            menuTrigger="focus"
            allowsEmptyCollection={true}
            isDisabled={disabled}>
            {label ? <Label className="mb-1 block">{label}</Label> : null}
            <Group
                className={`form-input flex w-full items-center rounded-sm border-gray-300 hover:border-brand-600 focus-within:ring-2 focus-within:ring-brand-300 md:rounded ${
                    disabled ? "bg-gray-100" : ""
                }`}>
                <Input
                    data-testid="time-picker-input"
                    placeholder="Select time"
                    className="w-full min-w-0 flex-1 bg-transparent p-0 outline-none placeholder:text-gray-400"
                />
                {normalizedValue && !disabled ? (
                    <Button
                        slot={null}
                        data-testid="time-picker-clear"
                        onPress={() => onChange(null)}
                        className="px-1 text-gray-400 outline-none hover:text-brand-600">
                        <XMarkIcon className="h-4 w-4" />
                    </Button>
                ) : null}
                <Button
                    data-testid="time-picker-trigger"
                    className="px-1 text-gray-500 outline-none hover:text-brand-600">
                    <ClockIcon className="h-5 w-5" />
                </Button>
            </Group>
            <Popover
                data-testid="time-picker-popover"
                isNonModal={true}
                maxHeight={500}
                className="z-1600 overflow-hidden rounded border border-gray-200 bg-white shadow-lg outline-none">
                <TimeListBox anchorValue={normalizedValue ?? DEFAULT_TIME_LIST_ANCHOR} />
            </Popover>
        </ComboBox>
    );
};

export const TimePicker: React.FC<TimePickerProps> = props => {
    const isTouchPrimary = useIsTouchPrimary();
    return isTouchPrimary ? (
        <NativeTimePicker {...props} />
    ) : (
        <DesktopTimePicker {...props} />
    );
};
